<script setup>
import { ref, computed } from 'vue';

// Prominent "came from ad" card rendered at the very top of the conversation
// thread (above the first message), so the agent immediately sees which
// Facebook/Instagram ad or post the customer came from — including the post
// image and the first lines of the ad copy. Built from
// conversation.additional_attributes.ad_referral (captured at ingestion and
// enriched with campaign name + spend + a hosted image by
// AdReferralEnrichmentJob). Only rendered when ad_referral exists, so it never
// shows for organic chats.
const props = defineProps({
  referral: {
    type: Object,
    required: true,
  },
});

const imgFailed = ref(false);

const isPost = computed(() => props.referral.type === 'post');

// Prefer the durably-hosted image; fall back to the raw (expiring) thumbnail.
const imageSrc = computed(
  () => props.referral.image_url || props.referral.thumbnail_url || ''
);

const title = computed(
  () =>
    props.referral.campaign_name ||
    props.referral.ad_name ||
    props.referral.headline ||
    'إعلان'
);

// Prefer the ad's actual copy (fetched from Meta) over the referral body.
// Facebook ad copy can contain ** bold markers — strip them for clean display.
const bodyText = computed(() =>
  (props.referral.ad_text || props.referral.body || '').replace(/\*\*/g, '')
);
const spend = computed(() => props.referral.spend);
const adUrl = computed(() => props.referral.url);
const postUrl = computed(() => props.referral.post_url);

// "How much each conversation costs me on this ad" (spend ÷ conversations
// started) — the headline metric, matching Ads Manager's Cost per result.
const costPerConversation = computed(
  () => props.referral.cost_per_conversation
);
const conversationsStarted = computed(
  () => props.referral.conversations_started
);

// The ad's welcome-flow questions + prepared answers (reconstructed from the ad).
const welcomeFaqs = computed(() =>
  Array.isArray(props.referral.welcome_faqs) ? props.referral.welcome_faqs : []
);
const faqsOpen = ref(false);

// Arabic copy kept in script (not bare in template) to satisfy the lint rule.
const sourceLabel = computed(() =>
  isPost.value ? '🎯 جه من منشور' : '🎯 جه من إعلان'
);
const costLabel = '💰 تكلفة الرسالة في الإعلان';
const spendLabel = 'صرف:';
const conversationsLabel = 'محادثة';
const dotLabel = '·';
const currencyLabel = 'جنيه';
const adLinkLabel = 'شوف الإعلان';
const postLinkLabel = 'شوف المنشور';
const faqsToggleLabel = computed(
  () => `❓ أسئلة وإجابات الإعلان (${welcomeFaqs.value.length})`
);
const faqsArrow = computed(() => (faqsOpen.value ? '▾' : '▸'));
</script>

<template>
  <div
    class="flex flex-col w-full gap-2 p-3 mx-auto mt-2 mb-1 border shadow-sm max-w-[30rem] border-n-weak rounded-xl bg-n-background"
  >
    <div class="flex items-center gap-1.5">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ sourceLabel }}
      </span>
    </div>

    <!-- Headline: how much each conversation costs on this ad -->
    <div
      v-if="costPerConversation"
      class="flex flex-col gap-1 px-4 py-3 border rounded-xl bg-n-brand/10 border-n-brand"
    >
      <span class="text-xs font-semibold text-n-slate-11" dir="auto">
        {{ costLabel }}
      </span>
      <p class="flex items-baseline gap-1.5" dir="auto">
        <span
          class="text-3xl font-extrabold leading-none text-n-brand"
          dir="ltr"
        >
          {{ costPerConversation }}
        </span>
        <span class="text-base font-bold text-n-brand">
          {{ currencyLabel }}
        </span>
      </p>
      <p v-if="spend" class="text-xs text-n-slate-11" dir="auto">
        {{ spendLabel }} <span dir="ltr">{{ spend }}</span> {{ currencyLabel }}
        <template v-if="conversationsStarted">
          {{ dotLabel }} <span dir="ltr">{{ conversationsStarted }}</span>
          {{ conversationsLabel }}
        </template>
      </p>
    </div>

    <img
      v-if="imageSrc && !imgFailed"
      :src="imageSrc"
      referrerpolicy="no-referrer"
      class="object-cover w-full rounded-lg max-h-64 bg-n-alpha-2"
      @error="imgFailed = true"
    />

    <p class="text-sm font-medium leading-snug text-n-slate-12" dir="auto">
      {{ title }}
    </p>

    <p
      v-if="bodyText"
      class="text-xs leading-relaxed text-n-slate-11 line-clamp-3 whitespace-pre-line"
      dir="auto"
    >
      {{ bodyText }}
    </p>

    <!-- The ad's welcome flow: the questions the customer saw + their answers -->
    <div v-if="welcomeFaqs.length" class="flex flex-col">
      <button
        type="button"
        class="flex items-center gap-1 text-xs font-medium text-start text-n-brand"
        @click="faqsOpen = !faqsOpen"
      >
        {{ faqsToggleLabel }}
        <span dir="ltr">{{ faqsArrow }}</span>
      </button>
      <div v-if="faqsOpen" class="flex flex-col gap-2 mt-2">
        <div
          v-for="(faq, i) in welcomeFaqs"
          :key="i"
          class="flex flex-col gap-0.5 ps-2 border-s-2 border-n-weak"
        >
          <p
            v-if="faq.q"
            class="text-xs font-semibold text-n-slate-12"
            dir="auto"
          >
            {{ faq.q }}
          </p>
          <p
            v-if="faq.a"
            class="text-xs leading-relaxed text-n-slate-11 whitespace-pre-line"
            dir="auto"
          >
            {{ faq.a }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 mt-1">
      <a
        v-if="adUrl"
        :href="adUrl"
        target="_blank"
        rel="noopener noreferrer nofollow"
        class="text-xs text-n-brand"
      >
        {{ adLinkLabel }}
      </a>
      <a
        v-if="postUrl"
        :href="postUrl"
        target="_blank"
        rel="noopener noreferrer nofollow"
        class="text-xs text-n-brand"
      >
        {{ postLinkLabel }}
      </a>
    </div>
  </div>
</template>
